import { createContext, FC, PropsWithChildren, useContext } from 'react';
import { useSelector } from 'react-redux';
import { selectIsBossOrSalesBossLevel, selectUserRbacPermissions } from 'core/userContext';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { useFacebookAdAccounts } from './facebookAdAccounts';
import {
  SEGMENTATION_AI_DESCRIPTIONS,
  CATALOG_IN_SEGMENTATION_DYNAMIC_LIST_FEATURE_FLAG,
} from '../featureFlags';

type PermissionsState = {
  canExportDiagnosticInformation: boolean;
  canManageSegmentation: boolean;
  canManageUserProfiles: boolean;
  canSaveDynamicListWithCatalog: boolean;
  canUseSegmentationAiDescriptions: boolean;
  canViewAndExpUserData: boolean;
  hasFacebookIntegration: boolean;
};

const defaultPermissionsState = {
  canExportDiagnosticInformation: false,
  canManageSegmentation: false,
  canManageUserProfiles: false,
  canUseSegmentationAiDescriptions: false,
  canViewAndExpUserData: false,
  hasFacebookIntegration: false,
  canSaveDynamicListWithCatalog: false,
};

export const PermissionsContext = createContext<PermissionsState>(defaultPermissionsState);

export const usePermissionsContext = () => useContext(PermissionsContext);

export const PermissionsContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  // permissions
  const permissions: number[] = useSelector(selectUserRbacPermissions);
  const canManageSegmentation = permissions.includes(PROJECT_PERMISSIONS.ManageSegmentation);
  const canManageUserProfiles = permissions.includes(PROJECT_PERMISSIONS.ManageUserProfiles);
  const canViewAndExpUserData = permissions.includes(PROJECT_PERMISSIONS.ViewAndExportUserData);

  const { fbAdAccounts = [] } = useFacebookAdAccounts();
  const hasFacebookIntegration = !!fbAdAccounts?.length;
  // boss
  /**
   * We are guarding access to these boss controls with general boss access
   * which is granted to employees at large and encompasses all granular
   * boss permissions, including the one used for the original implementation
   * in Angular, `ViewAsUserAdmin`, as the granular permissions are not
   * exposed or used anywhere in Reactland currently.
   *
   * For more context: https://iterable.slack.com/archives/CFGSACSH3/p1684278417074639
   */
  const hasBossAccess = useSelector(selectIsBossOrSalesBossLevel);
  const canExportDiagnosticInformation = Boolean(hasBossAccess);

  // feature flags
  const canUseSegmentationAiDescriptions = useFeatureFlag(SEGMENTATION_AI_DESCRIPTIONS);
  const canSaveDynamicListWithCatalog = useFeatureFlag(
    CATALOG_IN_SEGMENTATION_DYNAMIC_LIST_FEATURE_FLAG,
  );

  const value = {
    canExportDiagnosticInformation,
    canManageSegmentation,
    canManageUserProfiles,
    canUseSegmentationAiDescriptions,
    canViewAndExpUserData,
    hasFacebookIntegration,
    canSaveDynamicListWithCatalog,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};
